import { useState } from "react";
import type { EventShareItem } from "common";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import Drawer from "@via/components/drawer";
import { DrawerSize } from "@via/components/table/table";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  eventErrorMessage,
  eventErrorStatus,
  useDeleteVmetaEvent,
  useVmetaEventSharing,
} from "@via/pages/projects/metadata/events/events.api";
import EventShareDialog from "./event-share-dialog";
import EventDefinitionEditor from "./event-definition-editor";

const sharedLabel = (item: EventShareItem) =>
  item.perms.length ? `Shared with ${item.perms.length}` : "Not shared";

const buttonClass =
  "rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50";

interface Props {
  canvasId: string;
  /** Configuration mounts every tab, so the list is fetched only while this tab is shown. */
  isActive: boolean;
}

const EventFormsConfig = ({ canvasId, isActive }: Props) => {
  const { data, isLoading, error } = useVmetaEventSharing(canvasId, isActive);
  const { setNotification } = useNotifications();
  const { mutateAsync: deleteEvent, isPending: deleting } =
    useDeleteVmetaEvent();
  const [openId, setOpenId] = useState<string | null>(null);
  // Null when the drawer is closed; `eventId` null inside it means a new form.
  const [editor, setEditor] = useState<{ eventId: string | null } | null>(null);
  const [toDelete, setToDelete] = useState<EventShareItem | null>(null);

  if (isLoading)
    return <p className="p-4 text-sm text-gray-500">Loading event forms.</p>;
  if (error) {
    return (
      <p className="p-4 text-sm text-gray-700">
        {eventErrorStatus(error) === 403
          ? "Sharing event forms needs write access to this sample tracker."
          : "Event forms could not be loaded."}
      </p>
    );
  }

  const remove = async (): Promise<boolean> => {
    if (!toDelete) return true;
    try {
      await deleteEvent({
        canvasId,
        eventId: toDelete.id,
        version: toDelete.version,
      });
      setNotification({
        id: "event-definition-deleted",
        message: `${toDelete.name} deleted`,
        variant: NOTIFICATION.SUCCESS,
      });
      return true;
    } catch (err) {
      setNotification({
        id: "event-definition-delete-failed",
        message: eventErrorMessage(err, "The event form could not be deleted."),
        variant: NOTIFICATION.ERROR,
      });
      return false;
    }
  };

  const open = data?.find(e => e.id === openId);
  return (
    <>
      <div className="flex justify-end px-4 pt-4">
        <button
          type="button"
          onClick={() => setEditor({ eventId: null })}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          New event form
        </button>
      </div>
      {!data?.length ? (
        <p className="p-4 text-sm text-gray-700">
          This sample tracker has no event forms.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 p-4">
          {data.map(e => (
            <li key={e.id} className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-900">
                {e.name}
              </span>
              <span className="flex items-center gap-4">
                <span className="text-sm text-gray-500">{sharedLabel(e)}</span>
                <button
                  type="button"
                  onClick={() => setEditor({ eventId: e.id })}
                  className={buttonClass}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setOpenId(e.id)}
                  className={buttonClass}>
                  Share
                </button>
                <button
                  type="button"
                  onClick={() => setToDelete(e)}
                  className={buttonClass}>
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <EventShareDialog
          canvasId={canvasId}
          item={open}
          open
          onClose={() => setOpenId(null)}
        />
      )}
      <Drawer
        open={editor !== null}
        setOpen={o => !o && setEditor(null)}
        size={DrawerSize.Big}
        showMinimizeButton={false}
        title={editor?.eventId === null ? "New event form" : "Edit event form"}>
        {/* Mounted only while open, so the editor fetches its options only then. */}
        {editor ? (
          <EventDefinitionEditor
            key={editor.eventId ?? "new"}
            canvasId={canvasId}
            eventId={editor.eventId}
            onClose={() => setEditor(null)}
          />
        ) : (
          <></>
        )}
      </Drawer>
      <DeleteModal
        item={toDelete}
        setItem={setToDelete}
        deleteFunction={remove}
        isLoading={deleting}
        title="Delete event form"
        message={name =>
          `Delete ${name}? People will no longer see this form. Barcodes that open this form will open the record instead.`
        }
      />
    </>
  );
};

export default EventFormsConfig;
